import React, { useMemo, useState } from 'react';
import { usePos } from '../context/PosContext';
import {
  LABEL_SIZES,
  CONTENT_OPTIONS,
  ContentKey,
  sizeToPx,
  sizeToMm,
  customLabelSize,
  getBarcodeSettings,
  saveBarcodeSettings,
  BarcodeLabelSettings,
  LabelLanguage,
  PriceStyle,
} from '../services/barcode';
import { resolveLabelTarget } from '../services/labelPrint';
import './Sections.css';
import './BarcodePrinting.css';

// Barcode Settings (content_barcode_setting_layout.xml): a 3-pane configurator —
// Label Size · Customize Content (incl. "Without Price Label") · live Barcode
// Preview. Saves defaults used by the Barcode Printing screen.
const BarcodeSettings: React.FC = () => {
  const { products, formatMoney, config } = usePos();
  const [s, setS] = useState<BarcodeLabelSettings>(() => getBarcodeSettings());
  const [savedSnapshot, setSavedSnapshot] = useState(() => JSON.stringify(getBarcodeSettings()));

  const dirty = JSON.stringify(s) !== savedSnapshot;

  const toggle = (k: ContentKey) =>
    setS((prev) => ({ ...prev, content: { ...prev.content, [k]: !prev.content[k] } }));

  const save = () => {
    saveBarcodeSettings(s);
    setSavedSnapshot(JSON.stringify(s));
  };

  // Which printer/language labels will actually go to with the current setting.
  const target = resolveLabelTarget(s.labelLanguage);

  const dims = sizeToPx(s.labelSize);
  // Sample product for the preview (first one with a barcode, else first).
  const sample = useMemo(
    () => products.find((p) => p.barcode) ?? products[0],
    [products]
  );
  const code = sample?.barcode || (sample ? String(sample.id) : '0000000000000');

  const Bars = ({ value }: { value: string }) => (
    <div className="bc-bars">
      {(value || '0').split('').map((ch, idx) => (
        <span key={idx} style={{ width: `${(ch.charCodeAt(0) % 4) + 1}px` }} />
      ))}
    </div>
  );

  const rows = (): (PriceStyle & { label: string; value: string })[] => {
    if (!sample) return [];
    const plain = { bold: false, scale: 1 as const };
    const out: (PriceStyle & { label: string; value: string })[] = [];
    if (s.content.shortName && sample.shortName)
      out.push({ label: 'Short Name', value: sample.shortName, ...plain });
    if (s.content.price && !s.withoutPrice)
      out.push({ label: 'Price', value: formatMoney(sample.price), ...s.priceStyle });
    if (s.content.mrp) out.push({ label: 'MRP', value: formatMoney(sample.mrpPrice ?? sample.price), ...plain });
    if (s.content.purchasePrice && sample.costPrice != null)
      out.push({ label: 'Pur. Price', value: formatMoney(sample.costPrice), ...plain });
    if (s.content.hsn && sample.hsnCode) out.push({ label: 'HSN', value: sample.hsnCode, ...plain });
    if (s.content.searchCode) out.push({ label: 'Code', value: String(sample.id), ...plain });
    return out;
  };

  return (
    <div className="section-page bc-page">
      <div className="page-header">
        <h1>Barcode Settings</h1>
        <button className="btn-teal" onClick={save} disabled={!dirty}>
          {dirty ? 'Save Settings' : 'Saved'}
        </button>
      </div>

      <div className="bc-grid">
        {/* ===== Label Size ===== */}
        <div className="bc-panel">
          <div className="bc-panel-head">Label Size</div>
          <div className="bc-panel-body">
            <div className="bc-size-list">
              {LABEL_SIZES.map((l) => (
                <button
                  key={l}
                  className={`bc-radio ${s.labelSize === l ? 'active' : ''}`}
                  onClick={() => setS((prev) => ({ ...prev, labelSize: l }))}
                >
                  <span className="bc-radio-dot" />
                  {l}
                </button>
              ))}
              {!LABEL_SIZES.includes(s.labelSize) && (
                <button className="bc-radio active">
                  <span className="bc-radio-dot" />
                  {s.labelSize} (custom)
                </button>
              )}
            </div>

            <div className="bc-field-label">Or enter your own size (mm)</div>
            <div className="bc-divider" />
            <div className="bc-gen-row">
              <input
                className="bc-input"
                type="number"
                min={10}
                value={sizeToMm(s.labelSize).w}
                title="Label width in mm"
                onChange={(e) =>
                  setS((prev) => ({
                    ...prev,
                    labelSize: customLabelSize(Number(e.target.value) || 0, sizeToMm(prev.labelSize).h),
                  }))
                }
              />
              <input
                className="bc-input"
                type="number"
                min={10}
                value={sizeToMm(s.labelSize).h}
                title="Label height in mm"
                onChange={(e) =>
                  setS((prev) => ({
                    ...prev,
                    labelSize: customLabelSize(sizeToMm(prev.labelSize).w, Number(e.target.value) || 0),
                  }))
                }
              />
            </div>

            <div className="bc-field-label">Labels across the roll</div>
            <div className="bc-divider" />
            <div className="bc-gen-row">
              <select
                className="bc-input"
                value={s.labelsAcross}
                onChange={(e) => setS((prev) => ({ ...prev, labelsAcross: Number(e.target.value) }))}
              >
                <option value={1}>1 label across</option>
                <option value={2}>2 labels across (2-up)</option>
                <option value={3}>3 labels across</option>
              </select>
              <input
                className="bc-input"
                type="number"
                min={0}
                step={0.5}
                value={s.columnGapMm}
                title="Gap between the columns, in mm"
                onChange={(e) =>
                  setS((prev) => ({ ...prev, columnGapMm: Math.max(0, parseFloat(e.target.value) || 0) }))
                }
              />
            </div>

            <div className="bc-field-label">Gap between labels (mm)</div>
            <div className="bc-divider" />
            <input
              className="bc-input"
              type="number"
              min={0}
              step={0.5}
              value={s.labelGapMm}
              title="The blank strip between two labels on the roll — the printer's gap sensor uses this"
              onChange={(e) =>
                setS((prev) => ({ ...prev, labelGapMm: Math.max(0, parseFloat(e.target.value) || 0) }))
              }
            />

            <div className="bc-field-label">Label margins — top / left / bottom / right (mm)</div>
            <div className="bc-divider" />
            <div className="bc-gen-row">
              <input
                className="bc-input"
                type="number"
                min={0}
                step={0.5}
                value={s.marginTopMm}
                title="Blank space at the top of the label"
                onChange={(e) =>
                  setS((prev) => ({ ...prev, marginTopMm: Math.max(0, parseFloat(e.target.value) || 0) }))
                }
              />
              <input
                className="bc-input"
                type="number"
                min={0}
                step={0.5}
                value={s.marginLeftMm}
                title="Blank space at the left edge of the label"
                onChange={(e) =>
                  setS((prev) => ({ ...prev, marginLeftMm: Math.max(0, parseFloat(e.target.value) || 0) }))
                }
              />
              <input
                className="bc-input"
                type="number"
                min={0}
                step={0.5}
                value={s.marginBottomMm}
                title="Blank space kept at the bottom of the label — 0 uses the full height"
                onChange={(e) =>
                  setS((prev) => ({ ...prev, marginBottomMm: Math.max(0, parseFloat(e.target.value) || 0) }))
                }
              />
              <input
                className="bc-input"
                type="number"
                min={0}
                step={0.5}
                value={s.marginRightMm}
                title="Blank space kept at the right edge — 0 uses the full width"
                onChange={(e) =>
                  setS((prev) => ({ ...prev, marginRightMm: Math.max(0, parseFloat(e.target.value) || 0) }))
                }
              />
            </div>

            <div className="bc-field-label">Label printer language</div>
            <div className="bc-divider" />
            <select
              className="bc-input"
              value={s.labelLanguage}
              onChange={(e) =>
                setS((prev) => ({ ...prev, labelLanguage: e.target.value as LabelLanguage }))
              }
            >
              <option value="auto">Auto — TSPL on a label printer, ESC/POS on the receipt printer</option>
              <option value="escpos">ESC/POS (normal thermal receipt printer)</option>
              <option value="tspl">TSPL (TSC-style barcode label printer)</option>
            </select>
            <p className="muted" style={{ marginTop: 6 }}>
              {target
                ? `Labels print to ${target.printer.label || 'the USB printer'} as ${
                    target.lang === 'tspl' ? 'TSPL' : 'ESC/POS'
                  }${target.viaReceiptPrinter ? ' (the receipt printer — no separate label printer is set).' : '.'}`
                : 'No USB printer is set yet — labels fall back to the browser print sheet. Set one under USB Devices.'}
            </p>
          </div>
        </div>

        {/* ===== Customize Content ===== */}
        <div className="bc-panel">
          <div className="bc-panel-head">Customize Content</div>
          <div className="bc-panel-body">
            <label className="bc-check">
              <input
                type="checkbox"
                checked={s.withoutPrice}
                onChange={(e) => setS((prev) => ({ ...prev, withoutPrice: e.target.checked }))}
              />
              Without Price Label
            </label>
            <div className="bc-divider" />
            <div className="bc-content-list">
              {CONTENT_OPTIONS.map((o) => (
                <label key={o.key} className="bc-check">
                  <input
                    type="checkbox"
                    checked={s.content[o.key]}
                    disabled={o.key === 'price' && s.withoutPrice}
                    onChange={() => toggle(o.key)}
                  />
                  {o.label}
                </label>
              ))}
            </div>

            <div className="bc-field-label">Price emphasis</div>
            <div className="bc-divider" />
            <label className="bc-check">
              <input
                type="checkbox"
                checked={s.priceStyle.bold}
                disabled={s.withoutPrice || !s.content.price}
                onChange={(e) =>
                  setS((prev) => ({ ...prev, priceStyle: { ...prev.priceStyle, bold: e.target.checked } }))
                }
              />
              Bold Price
            </label>
            <select
              className="bc-input"
              value={s.priceStyle.scale}
              disabled={s.withoutPrice || !s.content.price}
              onChange={(e) =>
                setS((prev) => ({
                  ...prev,
                  priceStyle: { ...prev.priceStyle, scale: Number(e.target.value) as PriceStyle['scale'] },
                }))
              }
            >
              <option value={1}>Normal font</option>
              <option value={2}>Big font (2x)</option>
              <option value={3}>Extra big font (3x)</option>
            </select>
            <p className="muted" style={{ marginTop: 6 }}>
              Applies to the Price line on printed labels and the preview.
            </p>
          </div>
        </div>

        {/* ===== Barcode Preview ===== */}
        <div className="bc-panel">
          <div className="bc-panel-head">Barcode Preview</div>
          <div className="bc-panel-body">
            <div className="bc-preview-stage">
              {sample ? (
                <div
                  className="bc-label"
                  style={{
                    width: dims.w,
                    minHeight: dims.h,
                    paddingTop: `${s.marginTopMm * 3.4}px`,
                    paddingLeft: `${s.marginLeftMm * 3.4}px`,
                    paddingBottom: `${s.marginBottomMm * 3.4}px`,
                    paddingRight: `${s.marginRightMm * 3.4}px`,
                  }}
                >
                  {s.content.shopName && <div className="bc-store">{config.storeName}</div>}
                  {s.content.productName && <div className="bc-name">{sample.name}</div>}
                  {s.position === 'Bottom' ? (
                    <>
                      {rows().map((r) => (
                        <div
                          className="bc-row"
                          key={r.label}
                          style={{
                            fontWeight: r.bold ? 700 : undefined,
                            fontSize: r.scale > 1 ? `${r.scale * 9}px` : undefined,
                          }}
                        >
                          <span>{r.label}</span>
                          <span>{r.value}</span>
                        </div>
                      ))}
                      <Bars value={code} />
                      <div className="bc-code">{code}</div>
                    </>
                  ) : (
                    <>
                      <Bars value={code} />
                      <div className="bc-code">{code}</div>
                      {rows().map((r) => (
                        <div
                          className="bc-row"
                          key={r.label}
                          style={{
                            fontWeight: r.bold ? 700 : undefined,
                            fontSize: r.scale > 1 ? `${r.scale * 9}px` : undefined,
                          }}
                        >
                          <span>{r.label}</span>
                          <span>{r.value}</span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              ) : (
                <span className="muted">Add a product to preview the label.</span>
              )}
            </div>
            <p className="muted" style={{ marginTop: 12 }}>
              These defaults are applied on the Barcode Printing screen.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BarcodeSettings;
